import type { Cube, Meta } from '@cubejs-client/core'
import { createContext, useContext, useMemo, type ReactNode } from 'react'

import { Loading } from '@/components/feedback/Loading'
import type { ReportFilters } from '@/features/report/use-report-filters'
import { ChartWidgetView } from '@/features/report/widgets/ChartWidget'
import { KpiWidgetView } from '@/features/report/widgets/KpiWidget'
import { ListWidgetView } from '@/features/report/widgets/ListWidget'
import { TableWidgetView } from '@/features/report/widgets/TableWidget'
import {
  useChartData,
  useKpiData,
  useListData,
  useTableData,
} from '@/features/report/widgets/use-widget-data'
import { useCubeLiveQuery } from '@/hooks/use-cube-live-query'
import { buildCubeQuery } from '@/lib/cube'
import {
  viewFields,
  type ChartWidget,
  type KpiWidget,
  type ListWidget,
  type ReportField,
  type ReportFilterDef,
  type TableWidget,
} from '@/lib/cube-report'
import { messages } from '@/lib/messages'
import { periodLabel, previousPeriod } from '@/lib/period'
import { queryKeys } from '@/lib/query-keys'
import {
  documentFilterDefs,
  missingMembers,
  namedQuery,
  pageFilterDefs,
  withPeriod,
  type DocumentFilter,
  type DocumentQuery,
} from '@/services/cube/document'
import { toRows, type ReportFilterState } from '@/services/cube/report'

import {
  DocumentDataSourceProvider,
  type DocumentDataSource,
  type NamedResult,
  type WidgetNodeName,
} from './data-source'
import { buildDocumentWidget } from './widget-model'
import { WidgetNotice } from './WidgetNotice'

/*
 * CANLI veri kaynağı (şablon önizlemesi): her sorgu `buildCubeQuery` ile HTTP'den bir kez çekilir
 * (`live: false`, abonelik yok); anahtar `queryKeys.cube(view, …)`. Yetkisiz view'ın blokları
 * "yetkiniz yok" gösterir (Cube meta'sı izne göre süzülüdür).
 */

interface LiveConfig {
  /** Meta yüklenene kadar tanımsız. */
  views: ReadonlyMap<string, Cube> | undefined
  queries: Record<string, DocumentQuery>
  pageFilters: ReportFilterDef[]
  state: ReportFilterState
  /** Seçili dönem (`YYYY-MM`); `period` filtresi yoksa tanımsız. */
  period: string | undefined
}

const LiveConfigContext = createContext<LiveConfig | null>(null)

function useLiveConfig(): LiveConfig {
  const config = useContext(LiveConfigContext)
  if (!config) throw new Error('LiveDocumentSource içinde değil')
  return config
}

const NO_FIELDS: ReadonlyMap<string, ReportField> = new Map()
const IDLE_QUERY = buildCubeQuery({})

function useLiveNamedQuery(config: LiveConfig, name: string | undefined): NamedResult | undefined {
  const def = name === undefined ? undefined : config.queries[name]
  const cube = def && config.views?.get(def.view)
  const fields = useMemo(() => (cube ? viewFields(cube).fields : NO_FIELDS), [cube])
  const missing = def && cube ? missingMembers(def, fields) : []
  const ready = def !== undefined && cube !== undefined && missing.length === 0

  const query = ready ? namedQuery(def, fields, config.pageFilters, config.state) : IDLE_QUERY
  const current = useCubeLiveQuery({
    queryKey: queryKeys.cube(def?.view ?? '', { ...query }),
    query,
    select: toRows,
    enabled: ready,
    subscribe: false,
  })
  const wantsPrevious = ready && def.compare === 'previous' && config.period !== undefined
  const previousQuery =
    wantsPrevious && config.period
      ? namedQuery(
          def,
          fields,
          config.pageFilters,
          withPeriod(config.state, previousPeriod(config.period)),
        )
      : IDLE_QUERY
  const previous = useCubeLiveQuery({
    queryKey: queryKeys.cube(def?.view ?? '', { ...previousQuery }),
    query: previousQuery,
    select: toRows,
    enabled: wantsPrevious,
    subscribe: false,
  })

  if (!def) return undefined
  const base = { view: def.view, fields, rows: undefined }
  if (!config.views) {
    return { ...base, state: 'loading', status: { isLoading: true } }
  }
  if (!cube) return { ...base, state: 'forbidden', status: {} }
  if (missing.length > 0) {
    const error = new Error(messages.documents.editor.memberMissing(`${def.view}.${missing[0]}`))
    return { ...base, state: 'error', status: { isError: true, error } }
  }
  const status = {
    isLoading: current.isLoading || (wantsPrevious && previous.isLoading),
    isError: current.isError || previous.isError,
    error: current.error ?? previous.error,
    busy: current.isPlaceholderData || previous.isPlaceholderData,
  }
  return {
    ...base,
    rows: current.data,
    previousRows: previous.data,
    state: status.isError ? 'error' : status.isLoading ? 'loading' : 'ready',
    status,
  }
}

function KpiBody({
  widget,
  state,
  period,
}: {
  widget: KpiWidget
  state: ReportFilterState
  period: string | undefined
}) {
  const current = useKpiData(widget, state, { live: false })
  // Dönem filtresi varsa "önceki aya göre" (plan §4.4); yoksa aynı sorgu, ek istek olmaz.
  const previous = useKpiData(widget, period ? withPeriod(state, previousPeriod(period)) : state, {
    live: false,
  })
  const data = useMemo(
    () =>
      current.data && period && previous.data
        ? { ...current.data, previous: previous.data.value }
        : current.data,
    [current.data, previous.data, period],
  )
  return (
    <KpiWidgetView
      widget={widget}
      data={data}
      status={current.status}
      fxFailed={current.fxFailed}
    />
  )
}

function TableBody({ widget, state }: { widget: TableWidget; state: ReportFilterState }) {
  const { data, status } = useTableData(widget, state, { live: false })
  return <TableWidgetView widget={widget} data={data} status={status} />
}

function ListBody({
  widget,
  state,
  limit,
}: {
  widget: ListWidget
  state: ReportFilterState
  limit: number | undefined
}) {
  const { data, status } = useListData(widget, state, [], 1, limit ?? 50, { live: false })
  return <ListWidgetView widget={widget} data={data} status={status} />
}

function ChartBody({ widget, state }: { widget: ChartWidget; state: ReportFilterState }) {
  const { data, status } = useChartData(widget, state, { live: false })
  return <ChartWidgetView widget={widget} data={data} status={status} />
}

function LiveWidget({ type, attrs }: { type: WidgetNodeName; attrs: Record<string, unknown> }) {
  const { views, pageFilters, state, period } = useLiveConfig()
  const built = useMemo(
    () => views && buildDocumentWidget(type, attrs, views, pageFilters, period),
    [type, attrs, views, pageFilters, period],
  )
  if (!built) return <Loading rows={2} />
  if (!built.ok) {
    return (
      <WidgetNotice
        kind={built.reason}
        message={built.reason === 'invalid' ? built.message : undefined}
      />
    )
  }
  const { widget } = built
  switch (widget.type) {
    case 'kpi':
      return <KpiBody widget={widget} state={state} period={period} />
    case 'table':
      return <TableBody widget={widget} state={state} />
    case 'list':
      return <ListBody widget={widget} state={state} limit={built.limit} />
    case 'chart':
      return <ChartBody widget={widget} state={state} />
  }
}

/** `filters.<üye>` metni: seçili değerler virgülle, seçim yoksa "Tümü". */
function selectedText(filters: DocumentFilter[], state: ReportFilterState, member: string): string {
  const filter = filters.find(
    (item) => item.member === member && (item.type === 'multiSelect' || item.type === 'boolean'),
  )
  if (!filter) return messages.common.empty
  const key = `${filter.view}.${filter.member}`
  if (filter.type === 'boolean') {
    const flag = state.bool[key]
    if (flag === undefined) return messages.common.all
    return flag ? messages.common.yes : messages.common.no
  }
  const values = state.multi[key] ?? []
  return values.length > 0 ? values.join(', ') : messages.common.all
}

export function LiveDocumentSource({
  meta,
  filters,
  queries,
  reportFilters,
  children,
}: {
  meta: Meta | undefined
  filters: DocumentFilter[]
  queries: Record<string, DocumentQuery>
  reportFilters: ReportFilters
  children: ReactNode
}) {
  const views = useMemo(
    () =>
      meta &&
      new Map(meta.cubes.filter((cube) => cube.type === 'view').map((cube) => [cube.name, cube])),
    [meta],
  )
  const pageFilters = useMemo(() => pageFilterDefs(documentFilterDefs(filters)), [filters])
  const { state, period } = reportFilters
  const config = useMemo<LiveConfig>(
    () => ({ views, queries, pageFilters, state, period }),
    [views, queries, pageFilters, state, period],
  )
  const source = useMemo<DocumentDataSource>(
    () => ({
      periodLabel: period ? periodLabel(period) : undefined,
      filterText: (member) => selectedText(filters, state, member),
      useNamedQuery: (name) => useLiveNamedQuery(config, name),
      Widget: LiveWidget,
    }),
    [config, filters, state, period],
  )
  return (
    <LiveConfigContext.Provider value={config}>
      <DocumentDataSourceProvider value={source}>{children}</DocumentDataSourceProvider>
    </LiveConfigContext.Provider>
  )
}
